import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {Spinner} from '../../Spinner';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {MemoryContextPack} from './MemoryContextPack';
import {FACT_TYPE_ORDER, FACT_TYPE_MODIFIER, factTypeLabelKey, DETAIL_LEVEL, effectiveDetailLevel} from './factConstants';

// Máximo de hechos mostrados por tipo en el brief; el resto vive en la vista Hechos ("ver todos").
const FACTS_PER_TYPE = 3;
// Umbral de "hace mucho que no entra una reunión" para el bloque de pendientes.
const STALE_MEETING_DAYS = 14;

// `occurred_on` es un DateField ('YYYY-MM-DD'): new Date(iso) lo parsea como medianoche UTC y en
// UTC negativo (Argentina) la fecha se muestra corrida un día para atrás. Los date-only se
// construyen en LOCAL; los datetime completos (con hora/tz) van directo a new Date.
export function parseLocalDate(iso) {
    if (!iso) return null;
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (dateOnly) {
        return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    }
    return new Date(iso);
}

// "Resumen" — la vista default del tab Memoria en la ficha v2 (D3 del SSOT de UX): el brief
// vivo de qué sabe Dashbo del cliente. Para TOTAL: hechos aprobados agrupados por tipo. Para
// MEDIO (sin capa de Hechos): los últimos resúmenes/notas. Al costado: pendientes que
// interpelan, fuentes con frescura y el puente a la IA.
class MemoryResumenViewClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            brief: null,
            coverage: null,
            loading: true,
            failed: false,
        };
    }

    componentDidMount() {
        // `cache: true` en el fetch INICIAL: en la one-page del Recorrido esta vista y el
        // contenedor (`flow/BoosterFlowContent`, que es el dueño del conteo de hechos) conviven en
        // el mismo DOM y piden el MISMO brief —caro: hasta 300 hechos, más notas, reuniones y
        // conexiones—, así que sin el flag sería el mismo request dos veces; con él comparten el
        // que ya está en vuelo. Un "volver a cargar" explícito, si algún día se agrega acá, va SIN
        // cache (el patrón del repo: el refresh saltea el cache — si no, una respuesta de hace 30s
        // escondería lo recién curado o cargado).
        ClientMemoryApi.getMemoryBrief(this.props.clientId, {cache: true}).then((data) => {
            // El brief devuelve el payload directo (sin envoltorio) en data.data.
            if (data && data.status === 'OK') {
                this.setState({brief: data.data, loading: false});
            } else {
                this.setState({loading: false, failed: true});
            }
        });
        new APIClient(API_URL + 'client/' + this.props.clientId + '/activity-log/coverage/').call('GET')
            .then((data) => {
                if (data && data.status === 'OK') {
                    this.setState({coverage: data.data});
                }
            });
    }

    isTotal() {
        return this.state.brief && this.state.brief.level === 'TOTAL';
    }

    factGroups() {
        const facts = (this.state.brief && this.state.brief.facts) || [];
        // Dentro de cada tipo, los hechos GLOBALES primero: el top-3 del brief privilegia lo
        // estratégico sobre el detalle de plataforma (sort estable — el orden original se
        // conserva dentro de cada nivel; null cuenta como GLOBAL).
        const platformLast = (a, b) => (
            (effectiveDetailLevel(a) === DETAIL_LEVEL.PLATFORM ? 1 : 0)
            - (effectiveDetailLevel(b) === DETAIL_LEVEL.PLATFORM ? 1 : 0));
        return FACT_TYPE_ORDER
            .map(type => ({type, items: facts.filter(f => f.fact_type === type).sort(platformLast)}))
            .filter(group => group.items.length > 0);
    }

    lastMeetingDaysAgo() {
        const meetings = this.state.brief && this.state.brief.meetings;
        const last = meetings && meetings.last_meeting;
        if (!last) return null;
        const when = parseLocalDate(last.occurred_on || last.created_on);
        return Math.max(0, Math.floor((Date.now() - when.getTime()) / (24 * 3600 * 1000)));
    }

    memoryUrl(view) {
        return `/client/${this.props.clientId}/memory/${view}`;
    }

    // El brief está "vacío" (sin hechos aprobados en TOTAL o sin notas en MEDIO): la memoria
    // todavía no tiene material. En ese caso el empty state es el checklist del Paquete de Contexto.
    isBriefEmpty() {
        if (this.isTotal()) return this.factGroups().length === 0;
        return ((this.state.brief && this.state.brief.notes) || []).length === 0;
    }

    // Empty state = checklist "armá el contexto de este cliente" (Paquete de Contexto, mode full).
    // El copy del checklist es neutro (no menciona "hechos"), así sirve igual a MEDIO y a TOTAL.
    renderContextPackEmpty() {
        return (
            <MemoryContextPack
                clientId={this.props.clientId}
                clientName={this.props.clientName}
                mode="full"
            />
        );
    }

    renderBriefBlock() {
        const {t} = this.props;
        const brief = this.state.brief;
        if (this.isTotal()) {
            const groups = this.factGroups();
            if (groups.length === 0) {
                return this.renderContextPackEmpty();
            }
            return groups.map((group) => (
                <div key={group.type} className={`cmem-brief-group ${FACT_TYPE_MODIFIER[group.type] || ''}`}>
                    <h4 className="cmem-brief-group-title">
                        {t(factTypeLabelKey(group.type))} <span className="cmem-brief-count">{group.items.length}</span>
                    </h4>
                    {group.items.slice(0, FACTS_PER_TYPE).map((fact) => (
                        <p key={fact.id} className="cmem-brief-fact">{fact.statement}</p>
                    ))}
                    {group.items.length > FACTS_PER_TYPE && (
                        <Link className="cmem-brief-more" to={this.memoryUrl('facts')}>
                            {t('client_memory.brief.see_all', {count: group.items.length})}
                        </Link>
                    )}
                </div>
            ));
        }
        // Nivel MEDIO: sin capa de Hechos — el brief son los últimos resúmenes/notas.
        const notes = (brief && brief.notes) || [];
        if (notes.length === 0) {
            return this.renderContextPackEmpty();
        }
        return notes.map((note) => (
            <p key={note.id} className="cmem-brief-fact">{note.text}</p>
        ));
    }

    renderPendings() {
        const {t} = this.props;
        const brief = this.state.brief;
        const items = [];
        if (this.isTotal() && brief.pending_facts_count > 0) {
            items.push(
                <div key="pending-facts" className="cmem-pending">
                    <span>{t('client_memory.brief.pending_facts', {count: brief.pending_facts_count})}</span>
                    <Link className="cmem-pending-cta" to={this.memoryUrl('facts')}>
                        {t('client_memory.brief.review_cta')}
                    </Link>
                </div>
            );
        }
        const daysAgo = this.lastMeetingDaysAgo();
        const noMeetings = !brief || !brief.meetings || brief.meetings.ready_count === 0;
        if (noMeetings || daysAgo >= STALE_MEETING_DAYS) {
            items.push(
                <div key="stale-meeting" className="cmem-pending">
                    <span>
                        {noMeetings
                            ? t('client_memory.brief.no_meetings')
                            : t('client_memory.brief.stale_meeting', {count: daysAgo})}
                    </span>
                    <Link className="cmem-pending-cta" to={this.memoryUrl('setup')}>
                        {t('client_memory.add_menu.button')}
                    </Link>
                </div>
            );
        }
        if (items.length === 0) return null;
        return (
            <div className="cmem-brief-box">
                <h4 className="cmem-brief-box-title">{t('client_memory.brief.pendings_title')}</h4>
                {items}
            </div>
        );
    }

    renderSources() {
        const {t, i18n} = this.props;
        const brief = this.state.brief;
        if (!brief) return null;
        const meetings = brief.meetings || {};
        const connections = brief.drive_connections || [];
        const coverage = this.state.coverage;
        const platform = coverage && coverage.platform_changes;
        const formatDate = (iso) => {
            const date = parseLocalDate(iso);
            return date ? date.toLocaleDateString(i18n.language) : '—';
        };

        return (
            <div className="cmem-brief-box">
                <h4 className="cmem-brief-box-title">{t('client_memory.brief.sources_title')}</h4>
                <div className="cmem-brief-source">
                    <span>{t('client_memory.bitacora.coverage_meetings')}</span>
                    <span className="cmem-brief-source-detail">
                        {meetings.ready_count > 0
                            ? t('client_memory.brief.meetings_detail', {
                                count: meetings.ready_count,
                                date: formatDate(meetings.last_meeting && (meetings.last_meeting.occurred_on || meetings.last_meeting.created_on)),
                                interpolation: {escapeValue: false},
                            })
                            : t('client_memory.brief.none')}
                    </span>
                </div>
                <div className="cmem-brief-source">
                    <span>Google Drive</span>
                    <span className="cmem-brief-source-detail">
                        {connections.length > 0
                            ? t('client_memory.brief.drive_detail', {count: connections.length})
                            : t('client_memory.brief.none')}
                    </span>
                </div>
                {platform && platform.enabled && (
                    <div className="cmem-brief-source">
                        <span>{t('client_memory.brief.platform_changes')}</span>
                        <span className="cmem-brief-source-detail">
                            {(platform.channels || []).map(ch =>
                                `${ch.channel === 'GOOGLE' ? 'Google' : 'Meta'} ${ch.accounts_not_exposing > 0 ? '⚠' : '✓'}`
                            ).join(' · ') || t('client_memory.brief.none')}
                        </span>
                    </div>
                )}
                <Link className="cmem-brief-more" to={this.memoryUrl('sources')}>
                    {t('client_memory.brief.sources_cta')}
                </Link>
            </div>
        );
    }

    render() {
        const {t} = this.props;
        if (this.state.loading) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }
        if (this.state.failed || !this.state.brief) {
            // Error ≠ "sin datos": el fetch falló — no mostrar un placeholder engañoso.
            return (
                <div className="cmem-placeholder">
                    <p>{t('client_memory.brief.load_error')}</p>
                </div>
            );
        }
        // Memoria vacía: el empty state es el checklist del Paquete de Contexto como pantalla.
        // Sin el título "Qué sabe Dashbo" (todavía no sabe nada): el checklist trae su propia
        // jerarquía, y `-empty` anula el grid de 2 columnas del brief y lo centra.
        if (this.isBriefEmpty()) {
            return (
                <div className="cmem-brief -empty">
                    {this.renderContextPackEmpty()}
                </div>
            );
        }
        return (
            <div className="cmem-brief">
                <div className="cmem-brief-main">
                    {/* Contexto incompleto: strip compacto colapsable arriba del brief (se
                        auto-oculta si el CORE está completo). */}
                    <MemoryContextPack
                        clientId={this.props.clientId}
                        clientName={this.props.clientName}
                        mode="compact"
                    />
                    <div className="cmem-brief-header">
                        <h3 className="cmem-brief-title">{t('client_memory.brief.title')}</h3>
                        {this.isTotal() && (
                            <span className="cmem-brief-subtitle">
                                {t('client_memory.brief.facts_count', {count: (this.state.brief.facts || []).length})}
                            </span>
                        )}
                    </div>
                    {this.renderBriefBlock()}
                </div>
                <div className="cmem-brief-side">
                    {this.renderPendings()}
                    {this.renderSources()}
                    {/* Solo si el usuario tiene algún módulo de IA: sin AGENT ni MCP el CTA
                        rebotaría a /monitoring sin explicación. */}
                    {this.props.showIACta && (
                        <div className="cmem-brief-box">
                            <h4 className="cmem-brief-box-title">{t('client_memory.brief.ia_title')}</h4>
                            <p className="cmem-brief-ia-text">{t('client_memory.brief.ia_text')}</p>
                            <Link className="btn -alt cmem-empty-cta" to={`/client/${this.props.clientId}/ia`}>
                                {t('client_memory.brief.ia_cta')}
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export const MemoryResumenView = withTranslation()(MemoryResumenViewClass);
